/**
 * Seed data for the BusinessProfile mock API.
 *
 * These two profiles map to Module Catalog BÖLÜM 4 Example 1 (Salon Grooming)
 * and Example 2 (Fiziksel Veteriner). They are the same seeds that will be
 * inserted into `business_profiles` via migration when ADR-100 ships.
 */
import type {
  BusinessProfileRecord,
  DeploymentRecord,
} from './types';

const NOW = new Date().toISOString();
const YESTERDAY = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
const LAST_WEEK = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

export const SEED_PROFILES: BusinessProfileRecord[] = [
  {
    id: 'bp-groomer-salon-120',
    subVerticalKey: 'groomer_salon',
    displayName: 'Pet Kuaförü (Salon)',
    profileVersion: '1.2.0',
    engine: 'E02',
    deliveryMode: 'on_site',
    status: 'active',
    createdBy: 'usr-admin-01',
    createdAt: LAST_WEEK,
    activatedAt: YESTERDAY,
    checksumSha256: 'a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6q7r8s9t0',
    profile: {
      subVerticalKey: 'groomer_salon',
      displayName: 'Pet Kuaförü (Salon)',
      profileVersion: '1.2.0',
      engine: 'E02',
      deliveryMode: 'on_site',
      scheduling: {
        slotDurations: [30, 45, 60, 90, 120],
        bufferMinutes: 15,
        multiResource: { enabled: true, resources: ['groomer', 'wash_station'] },
        overbookingPolicy: 'not_allowed',
      },
      modules: [
        'M01',
        'M02',
        'M04',
        'M05',
        'M06',
        'M07',
        'M08',
        'V-GRM-01',
        'V-GRM-02',
        'V-GRM-03',
        'C01',
        'A01',
      ],
      excludedModules: ['V-VET-01', 'V-HOME-01'],
      serviceFields: {
        required: ['petBreed', 'petWeight', 'coatType', 'serviceType'],
        optional: ['behaviorNotes', 'allergyNotes'],
      },
      pricingDefaults: {
        currency: 'TRY',
        pipeline: 'QuotePipeline/v1',
      },
      terminology: {
        customer: 'Müşteri',
        appointment: 'Randevu',
        service: 'Hizmet',
        staff: 'Kuaför',
      },
      reportTemplates: ['grooming_report_card'],
      compliance: {
        dataRetentionYears: 2,
        auditLogRequired: true,
      },
    },
  },
  {
    id: 'bp-vet-clinic-100',
    subVerticalKey: 'vet_clinic',
    displayName: 'Fiziksel Veteriner Kliniği',
    profileVersion: '1.0.0',
    engine: 'E01',
    deliveryMode: 'on_site',
    status: 'active',
    createdBy: 'usr-admin-01',
    createdAt: LAST_WEEK,
    activatedAt: LAST_WEEK,
    checksumSha256: 'b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6q7r8s9t0u1',
    profile: {
      subVerticalKey: 'vet_clinic',
      displayName: 'Fiziksel Veteriner Kliniği',
      profileVersion: '1.0.0',
      engine: 'E01',
      deliveryMode: 'on_site',
      scheduling: {
        slotDurations: [15, 20, 30, 45, 60],
        bufferMinutes: 5,
        multiResource: { enabled: true, resources: ['vet', 'exam_room'] },
        overbookingPolicy: 'emergency_only',
      },
      modules: [
        'M01',
        'M02',
        'M04',
        'M05',
        'M06',
        'M07',
        'M08',
        'V-VET-01',
        'V-VET-02',
        'V-VET-03',
        'V-VET-04',
        'C01',
        'C02',
        'A01',
        'A02',
      ],
      excludedModules: ['V-GRM-01', 'V-HOME-01', 'V-WALK-01'],
      serviceFields: {
        required: ['petSpecies', 'petBreed', 'petAge', 'chiefComplaint'],
        optional: ['vitalSigns', 'currentMedications', 'allergyHistory'],
      },
      pricingDefaults: {
        currency: 'TRY',
        pipeline: 'QuotePipeline/v1',
      },
      terminology: {
        customer: 'Hasta Sahibi',
        appointment: 'Muayene',
        service: 'İşlem',
        staff: 'Veteriner Hekim',
      },
      reportTemplates: ['medical_record', 'discharge_summary'],
      compliance: {
        requiredCertifications: ['vet_license_tr'],
        dataRetentionYears: 10,
        auditLogRequired: true,
      },
    },
  },
];

export const SEED_DEPLOYMENTS: DeploymentRecord[] = [
  {
    id: 'dep-01',
    profileId: 'bp-groomer-salon-120',
    subVerticalKey: 'groomer_salon',
    profileVersion: '1.2.0',
    deploymentMode: 'full',
    tenantScope: 'all',
    reason: 'v1.2.0 GA: grooming report card eklendi, buffer 10→15 güncellendi.',
    actorId: 'usr-admin-01',
    actorName: 'Operations CTO',
    correlationId: 'corr-groomer-120-full',
    createdAt: YESTERDAY,
  },
  {
    id: 'dep-02',
    profileId: 'bp-groomer-salon-120',
    subVerticalKey: 'groomer_salon',
    profileVersion: '1.2.0',
    deploymentMode: 'canary',
    tenantScope: 'percentage',
    percentage: 10,
    reason: 'v1.2.0 canary: sadece erken erişim partnerleri.',
    actorId: 'usr-admin-01',
    actorName: 'Operations CTO',
    correlationId: 'corr-groomer-120-canary',
    createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
  },
  {
    id: 'dep-03',
    profileId: 'bp-vet-clinic-100',
    subVerticalKey: 'vet_clinic',
    profileVersion: '1.0.0',
    deploymentMode: 'full',
    tenantScope: 'all',
    reason: 'v1.0.0 GA: başlangıç veteriner profili.',
    actorId: 'usr-admin-01',
    actorName: 'Operations CTO',
    correlationId: 'corr-vet-100-full',
    createdAt: LAST_WEEK,
  },
];

export const SEED_PROFILE_LAST_UPDATED = NOW;
